import { useState, useEffect, useRef } from "react";
import Snake from "./components/snake";
import Bait from "./components/bait";
import Obstacles1 from "./components/obstacle";
import Obstacles2 from "./components/obstacle";
import Obstacles3 from "./components/obstacle";
import Obstacles4 from "./components/obstacle";
import Obstacles5 from "./components/obstacle";
import Score from "./components/score";
import Instructions from "./components/instructions";
import GameOver from "./components/gameover";
import { getRandomPositionForBait } from "./utils/getRandomPositionForBait";
import { getRandomPositionForObstacle } from "./utils/getRandomPositionForObstacle";

function App() {
	const [bait, setBait] = useState(getRandomPositionForBait());
	const [obstacles1, setObstacles1] = useState(getRandomPositionForObstacle());
	const [obstacles2, setObstacles2] = useState(getRandomPositionForObstacle());
	const [obstacles3, setObstacles3] = useState(getRandomPositionForObstacle());
	const [obstacles4, setObstacles4] = useState(getRandomPositionForObstacle());
	const [obstacles5, setObstacles5] = useState(getRandomPositionForObstacle());
	const [speed, setSpeed] = useState(2);
	const [direction, setDirection] = useState("R");
	const [isGameOver, setIsGameOver] = useState(false);
	const [gameOverReason, setGameOverReason] = useState("");
	const [isNear, setIsNear] = useState(false);
	const [score, setScore] = useState(0);
	const [highScore, setHighScore] = useState(() => {
		try {
			const saved = localStorage.getItem("snakeHighScore");
			return saved ? JSON.parse(saved) : { 1: 0, 2: 0, 3: 0, 4: 0 };
		} catch (e) {
			return { 1: 0, 2: 0, 3: 0, 4: 0 };
		}
	});
	const [snake, setSnake] = useState(
		[...Array(5).keys()].map((x) => [0, 2 * x])
	);

	const directionRef = useRef(direction);
	const snakeRef = useRef(snake);
	const baitRef = useRef(bait);
	const isGameOverRef = useRef(isGameOver);
	const obstacles1Ref = useRef(obstacles1);
	const obstacles2Ref = useRef(obstacles2);
	const obstacles3Ref = useRef(obstacles3);
	const obstacles4Ref = useRef(obstacles4);
	const obstacles5Ref = useRef(obstacles5);
	const speedRef = useRef(speed);

	useEffect(() => {
		let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		let scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
		window.onscroll = function () {
			window.scrollTo(scrollLeft, scrollTop);
		};
	}, []);

	useEffect(() => { directionRef.current = direction; }, [direction]);
	useEffect(() => { snakeRef.current = snake; }, [snake]);
	useEffect(() => { baitRef.current = bait; }, [bait]);
	useEffect(() => { isGameOverRef.current = isGameOver; }, [isGameOver]);
	useEffect(() => { obstacles1Ref.current = obstacles1; }, [obstacles1]);
	useEffect(() => { obstacles2Ref.current = obstacles2; }, [obstacles2]);
	useEffect(() => { obstacles3Ref.current = obstacles3; }, [obstacles3]);
	useEffect(() => { obstacles4Ref.current = obstacles4; }, [obstacles4]);
	useEffect(() => { obstacles5Ref.current = obstacles5; }, [obstacles5]);
	useEffect(() => { speedRef.current = speed; }, [speed]);

	// persist highScore to localStorage
	useEffect(() => {
		try {
			localStorage.setItem("snakeHighScore", JSON.stringify(highScore));
		} catch (e) {
			// ignore storage errors
		}
	}, [highScore]);

	useEffect(() => {
		function handleKey(e) {
			const dir = directionRef.current;
			if (e.keyCode === 38 && dir !== "D") setDirection("U");
			if (e.keyCode === 39 && dir !== "L") setDirection("R");
			if (e.keyCode === 40 && dir !== "U") setDirection("D");
			if (e.keyCode === 37 && dir !== "R") setDirection("L");
		}
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, []);

	useEffect(() => {
		if (speedRef.current !== 0 && !isGameOverRef.current) {
			const interval = setInterval(() => {
				setSnake((prevSnake) => {
					const snakeArray = [...prevSnake];
					let headOfSnake = snakeArray[snakeArray.length - 1];
					const dir = directionRef.current;
					if (dir === "R") headOfSnake = [headOfSnake[0], headOfSnake[1] + 2];
					else if (dir === "L")
						headOfSnake = [headOfSnake[0], headOfSnake[1] - 2];
					else if (dir === "D")
						headOfSnake = [headOfSnake[0] + 2, headOfSnake[1]];
					else headOfSnake = [headOfSnake[0] - 2, headOfSnake[1]];

					// boundary check
					if (
						(headOfSnake[0] > 98 ||
							headOfSnake[1] > 98 ||
							headOfSnake[0] < 0 ||
							headOfSnake[1] < 0) &&
						!isGameOverRef.current
					) {
						clearInterval(interval);
						setIsGameOver(true);
						setGameOverReason("Boundary Collision!!!");
						return prevSnake;
					}

					// self collision
					if (JSON.stringify(snakeArray).indexOf(JSON.stringify(headOfSnake)) >= 0) {
						clearInterval(interval);
						setIsGameOver(true);
						setGameOverReason("Self Collision!!!");
						return prevSnake;
					}

					// obstacle collision
					const obsChecks = [
						obstacles1Ref.current,
						obstacles2Ref.current,
						obstacles3Ref.current,
						obstacles4Ref.current,
						obstacles5Ref.current,
					];
					if (
						obsChecks.some((obs) => JSON.stringify(obs).indexOf(JSON.stringify(headOfSnake)) >= 0)
					) {
						clearInterval(interval);
						setIsGameOver(true);
						setGameOverReason("Obstacle Collision!!!");
						return prevSnake;
					}

					snakeArray.push(headOfSnake);
					snakeArray.shift();

					// proximity
					const b = baitRef.current;
					if (Math.abs(b[0] - headOfSnake[0]) < 15 && Math.abs(b[1] - headOfSnake[1]) < 15) {
						setIsNear(true);
					} else {
						setIsNear(false);
					}

					// found bait
					if (b[0] === headOfSnake[0] && b[1] === headOfSnake[1]) {
						// grow snake
						snakeArray.push(headOfSnake);
						setBait(getRandomPositionForBait());
						setScore((prevScore) => {
							const newScore = prevScore + 1;
							setHighScore((prevHS) => ({
								...prevHS,
								[speedRef.current]: Math.max(prevHS[speedRef.current] || 0, newScore),
							}));
							return newScore;
						});
					}

					return snakeArray;
				});
			}, 200 / speedRef.current);

			return () => clearInterval(interval);
		}
	}, [speed, isGameOver]);

	function gameOver(reason) {
		clearInterval(window.myTimer);
		setIsGameOver(true);
		setGameOverReason(reason);
	}

	function reset() {
		setBait(getRandomPositionForBait());
		setSnake([...Array(5).keys()].map((x) => [0, 2 * x]));
		setDirection("R");
		setIsGameOver(false);
		setIsNear(false);
		setScore(0);
		setObstacles1(getRandomPositionForObstacle());
		setObstacles2(getRandomPositionForObstacle());
		setObstacles3(getRandomPositionForObstacle());
		setObstacles4(getRandomPositionForObstacle());
		setObstacles5(getRandomPositionForObstacle());
	}

	return (
		<div>
			<h1 className="title">SNAKE MANIA</h1>
			<div className="game-container">
				<div className="game-layout">
					<Snake snakeSquares={snake} />
					<Obstacles1 positions={obstacles1} />
					<Obstacles2 positions={obstacles2} />
					<Obstacles3 positions={obstacles3} />
					<Obstacles4 positions={obstacles4} />
					<Obstacles5 positions={obstacles5} />
					<Bait dot={bait} isNear={isNear} />
				</div>
				<div className="information">
					<Score currentScore={score} highScore={highScore} speed={speed} />
					<div style={{ display: 'contents' }}>
						<button onClick={() => reset()}>Reset</button>
					</div>
					<Instructions />
					<GameOver isGameOver={isGameOver} reason={gameOverReason} />
				</div>
			</div>
		</div>
	);
}

export default App;